import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Minus, Plus, ArrowUpRight, ShoppingBag, Truck } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth, API } from "@/lib/auth";

export default function Cart() {
  const { items, remove, setQty, subtotal, shipping, total, hasPhysical, count, add, clear } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [fulfilment, setFulfilment] = useState("delivery"); // "delivery" | "collection"
  const [addr, setAddr] = useState({
    name: "", line1: "", line2: "", city: "", postal_code: "", country: "GB",
  });
  const recoveredRef = useRef(false);
  const trackTimerRef = useRef(null);

  // Recover cart from ?recover=<token> (from abandoned-cart email)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("recover");
    if (!token || recoveredRef.current) return;
    recoveredRef.current = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/cart/recover/${token}`);
        // Restore items into cart (only add ones not already present)
        (r.data.items || []).forEach((it) => {
          add(
            { slug: it.slug, name: it.name, image: it.image, price: it.price, digital: it.digital },
            { length: it.length, colour: it.colour, quantity: it.quantity },
          );
        });
        if (r.data.email && !user) setGuestEmail(r.data.email);
        if (r.data.name && !user) setGuestName(r.data.name);
        toast.success("Your cart is back — welcome back!");
      } catch {
        toast.error("This recovery link has expired.");
      }
    })();
  }, [location.search, add, user]);

  // Track cart for abandoned-cart emails (debounced)
  const emailForTrack = user?.email || guestEmail;
  useEffect(() => {
    if (!emailForTrack || !items.length) return;
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    trackTimerRef.current = setTimeout(() => {
      const payload = {
        email: emailForTrack,
        name: user?.name || guestName || undefined,
        items: items.map((i) => ({
          slug: i.slug, name: i.name, quantity: i.quantity, price: i.price,
          image: i.image, length: i.length || undefined, colour: i.colour || undefined,
          digital: !!i.digital,
        })),
        subtotal, total,
        origin_url: window.location.origin,
      };
      axios.post(`${API}/cart/track`, payload).catch(() => {});
    }, 1200);
    return () => trackTimerRef.current && clearTimeout(trackTimerRef.current);
  }, [emailForTrack, items, subtotal, total, guestName, user]);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const r = await axios.post(`${API}/checkout/validate-code`, {
        code: promoInput.trim().toUpperCase(),
        subtotal, // £
        context: "shop",
      });
      if (r.data?.valid) {
        setPromo({
          code: promoInput.trim().toUpperCase(),
          kind: r.data.kind,
          label: r.data.label,
          discount: r.data.discount, // £
          percent_off: r.data.kind === "bundle" ? (subtotal > 0 ? (r.data.discount / subtotal) * 100 : 0) : null,
        });
        toast.success(`${r.data.label} applied`);
      } else {
        setPromo(null);
        toast.error(r.data?.message || "Invalid or expired code");
      }
    } catch {
      setPromo(null);
      toast.error("Invalid or expired code");
    } finally {
      setPromoLoading(false);
    }
  };

  const promoDiscount = promo ? Number(promo.discount || 0) : 0;
  const effectiveShipping = hasPhysical && fulfilment === "collection" ? 0 : shipping;
  const effectiveTotal = subtotal + effectiveShipping;
  const totalWithPromo = Math.max(0, effectiveTotal - promoDiscount);

  const checkout = async () => {
    if (!items.length) return;
    if (!user && !guestEmail) {
      toast.error("Enter your email to continue");
      return;
    }
    if (hasPhysical && fulfilment === "delivery") {
      const missing = ["name", "line1", "city", "postal_code"].filter((k) => !addr[k].trim());
      if (missing.length) {
        toast.error("Please complete your delivery address");
        return;
      }
    }
    if (hasPhysical && fulfilment === "collection") {
      if (!guestPhone.trim()) {
        toast.error("Please add a mobile number so we can notify you when your order is ready");
        return;
      }
    }
    setLoading(true);
    try {
      const isCollection = hasPhysical && fulfilment === "collection";
      const payload = {
        items: items.map((i) => ({
          slug: i.slug, quantity: i.quantity, length: i.length, colour: i.colour,
        })),
        origin_url: window.location.origin,
        guest_email: user ? undefined : guestEmail,
        guest_name: user ? undefined : guestName || undefined,
        guest_phone: hasPhysical && guestPhone.trim() ? guestPhone.trim() : undefined,
        promo_code: promo ? promo.code : undefined,
        fulfilment_type: hasPhysical ? fulfilment : undefined,
        shipping_address: hasPhysical && !isCollection
          ? {
              name: addr.name.trim(),
              line1: addr.line1.trim(),
              line2: addr.line2.trim() || undefined,
              city: addr.city.trim(),
              postal_code: addr.postal_code.trim().toUpperCase(),
              country: (addr.country || "GB").toUpperCase(),
            }
          : undefined,
      };
      const r = await axios.post(`${API}/shop/checkout`, payload);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  if (!items.length) {
    return (
      <div data-testid="page-cart" className="pt-40 pb-24 min-h-[60vh]">
        <div className="mx-auto max-w-[720px] px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-blush mx-auto flex items-center justify-center">
            <ShoppingBag size={32} className="text-[hsl(var(--onyx))]" />
          </div>
          <div className="overline mt-8">Your cart</div>
          <h1 className="font-display text-5xl mt-3 tracking-tight">
            Nothing here yet.
          </h1>
          <p className="mt-4 text-[hsl(var(--onyx)/0.6)]">
            Browse the shop to find your first premium salon essential.
          </p>
          <Link to="/shop" data-testid="cart-shop-link" className="btn-pill btn-primary mt-8 inline-flex">
            Explore the shop <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-cart" className="pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="overline">Your cart</div>
            <h1 className="font-display text-5xl sm:text-6xl mt-3 tracking-tight">
              {count} {count === 1 ? "item" : "items"}
            </h1>

            <div className="mt-10 rounded-[2rem] overflow-hidden bg-white border border-[hsl(var(--onyx)/0.06)]">
              {items.map((i, k) => (
                <div
                  key={`${i.slug}-${i.length || ""}-${i.colour || ""}`}
                  data-testid={`cart-item-${i.slug}`}
                  className={`p-6 flex flex-wrap sm:flex-nowrap items-center gap-5 ${k > 0 ? "border-t border-[hsl(var(--onyx)/0.06)]" : ""}`}
                >
                  <div className="w-24 h-24 rounded-2xl overflow-hidden bg-blush shrink-0">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-display text-xl">{i.name}</div>
                    {(i.length || i.colour) && (
                      <div className="mt-1 text-xs text-[hsl(var(--onyx)/0.55)]">
                        {[i.length, i.colourLabel || i.colour].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <div className="mt-2 text-sm text-[hsl(var(--onyx)/0.65)]">
                      £{i.price.toFixed(2)} each
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-3 rounded-full border border-[hsl(var(--onyx)/0.15)] px-3 py-1">
                    <button
                      onClick={() => setQty(i.slug, i.length, i.colour, i.quantity - 1)}
                      className="w-7 h-7 rounded-full hover:bg-[hsl(var(--onyx)/0.06)] flex items-center justify-center"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="min-w-[16px] text-center text-sm">{i.quantity}</span>
                    <button
                      onClick={() => setQty(i.slug, i.length, i.colour, i.quantity + 1)}
                      className="w-7 h-7 rounded-full hover:bg-[hsl(var(--onyx)/0.06)] flex items-center justify-center"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <div className="w-20 text-right font-medium">
                    £{(i.price * i.quantity).toFixed(2)}
                  </div>
                  <button
                    onClick={() => remove(i.slug, i.length, i.colour)}
                    aria-label="Remove"
                    className="w-8 h-8 rounded-full hover:bg-[hsl(var(--onyx)/0.06)] flex items-center justify-center text-[hsl(var(--onyx)/0.6)]"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="sticky top-28 rounded-[2rem] bg-white border border-[hsl(var(--onyx)/0.08)] p-8 shadow-[0_20px_60px_-30px_rgba(20,10,15,0.2)]">
              <div className="overline">Order summary</div>
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex items-center justify-between text-[hsl(var(--onyx)/0.7)]">
                  <span>Subtotal</span>
                  <span>£{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-[hsl(var(--onyx)/0.7)]">
                  <span>Shipping {hasPhysical ? (fulfilment === "collection" ? "(collection)" : "") : "(digital only)"}</span>
                  <span>{(!hasPhysical || fulfilment === "collection" || shipping === 0) ? "FREE" : `£${shipping.toFixed(2)}`}</span>
                </div>
                {hasPhysical && fulfilment === "delivery" && shipping > 0 && (
                  <div className="text-xs text-[hsl(var(--onyx)/0.5)] flex items-center gap-2">
                    <Truck size={12} className="text-[hsl(var(--gold))]" />
                    Spend £{(50 - subtotal).toFixed(2)} more for free shipping.
                  </div>
                )}
                {promo && (
                  <div className="flex items-center justify-between text-[hsl(var(--gold))]">
                    <span>{promo.label || `${promo.code}`}</span>
                    <span>−£{promoDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="pt-4 mt-4 border-t border-[hsl(var(--onyx)/0.08)] flex items-baseline justify-between">
                  <span className="text-[hsl(var(--onyx)/0.55)] text-xs uppercase tracking-widest">Total</span>
                  <span className="font-display text-3xl">£{totalWithPromo.toFixed(2)}</span>
                </div>
              </div>

              <div className="mt-6 space-y-2">
                <label className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">Promo code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter code"
                    data-testid="cart-promo-input"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    disabled={!!promo}
                    className="flex-1 h-11 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))] disabled:opacity-60"
                  />
                  {promo ? (
                    <button
                      onClick={() => { setPromo(null); setPromoInput(""); }}
                      data-testid="cart-promo-clear"
                      className="btn-pill btn-outline !text-xs !py-2"
                    >
                      Clear
                    </button>
                  ) : (
                    <button
                      onClick={applyPromo}
                      disabled={promoLoading}
                      data-testid="cart-promo-apply"
                      className="btn-pill btn-primary !text-xs !py-2"
                    >
                      Apply
                    </button>
                  )}
                </div>
              </div>

              {!user && (
                <div className="mt-8 space-y-3">
                  <div className="overline">Guest checkout</div>
                  <input
                    type="email"
                    placeholder="Your email"
                    data-testid="guest-email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    className="w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                  />
                  <input
                    type="text"
                    placeholder="Your name (optional)"
                    data-testid="guest-name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    className="w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                  />
                  <p className="text-xs text-[hsl(var(--onyx)/0.5)]">
                    We'll auto-create your account and email you a magic link to your library after payment.
                  </p>
                </div>
              )}

              {hasPhysical && (
                <div className="mt-8" data-testid="fulfilment-picker">
                  <div className="overline mb-3">How would you like this order?</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFulfilment("delivery")}
                      data-testid="fulfilment-delivery"
                      className={`h-auto py-3 px-4 rounded-2xl border text-left transition-colors ${fulfilment === "delivery" ? "border-[hsl(var(--onyx))] bg-blush" : "border-[hsl(var(--onyx)/0.15)] bg-white hover:border-[hsl(var(--onyx)/0.4)]"}`}
                    >
                      <div className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Deliver</div>
                      <div className="text-sm font-medium mt-0.5">Post to me</div>
                      <div className="text-[10px] text-[hsl(var(--onyx)/0.55)] mt-1">{shipping === 0 ? "Free — spend £50+" : `£${shipping.toFixed(2)}`}</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFulfilment("collection")}
                      data-testid="fulfilment-collection"
                      className={`h-auto py-3 px-4 rounded-2xl border text-left transition-colors ${fulfilment === "collection" ? "border-[hsl(var(--onyx))] bg-blush" : "border-[hsl(var(--onyx)/0.15)] bg-white hover:border-[hsl(var(--onyx)/0.4)]"}`}
                    >
                      <div className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Collect</div>
                      <div className="text-sm font-medium mt-0.5">The Wee Nook</div>
                      <div className="text-[10px] text-[hsl(var(--onyx)/0.55)] mt-1">Free · Email when ready</div>
                    </button>
                  </div>
                  {fulfilment === "collection" && (
                    <div className="mt-3 rounded-2xl bg-[hsl(var(--blush)/0.3)] border border-[hsl(var(--gold)/0.35)] px-4 py-3 text-xs text-[hsl(var(--onyx)/0.75)] leading-relaxed">
                      <div className="uppercase tracking-widest text-[10px] text-[hsl(var(--gold))] mb-1">Collect from</div>
                      <div className="font-medium text-sm text-[hsl(var(--onyx))]">The Wee Nook</div>
                      <div>58 Love Street, Paisley, PA3 2DY, Scotland</div>
                      <div className="mt-1 opacity-80">We'll email you as soon as your order is ready — usually within 1 working day.</div>
                    </div>
                  )}
                </div>
              )}

              {hasPhysical && fulfilment === "delivery" && (
                <div className="mt-8 space-y-3" data-testid="shipping-address">
                  <div className="overline">Delivery address</div>
                  <input
                    type="text"
                    placeholder="Full name"
                    data-testid="ship-name"
                    value={addr.name}
                    onChange={(e) => setAddr({ ...addr, name: e.target.value })}
                    className="w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                  />
                  <input
                    type="text"
                    placeholder="Address line 1"
                    data-testid="ship-line1"
                    value={addr.line1}
                    onChange={(e) => setAddr({ ...addr, line1: e.target.value })}
                    className="w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                  />
                  <input
                    type="text"
                    placeholder="Address line 2 (optional)"
                    data-testid="ship-line2"
                    value={addr.line2}
                    onChange={(e) => setAddr({ ...addr, line2: e.target.value })}
                    className="w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="City"
                      data-testid="ship-city"
                      value={addr.city}
                      onChange={(e) => setAddr({ ...addr, city: e.target.value })}
                      className="h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                    />
                    <input
                      type="text"
                      placeholder="Postcode"
                      data-testid="ship-postcode"
                      value={addr.postal_code}
                      onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })}
                      className="h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))] uppercase"
                    />
                  </div>
                  <p className="text-xs text-[hsl(var(--onyx)/0.5)]">
                    We currently ship within the United Kingdom.
                  </p>
                </div>
              )}

              {hasPhysical && fulfilment === "collection" && (
                <div className="mt-6 space-y-2" data-testid="collection-phone">
                  <div className="overline">Mobile number</div>
                  <input
                    type="tel"
                    placeholder="07…"
                    data-testid="collect-phone"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    className="w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                  />
                  <p className="text-xs text-[hsl(var(--onyx)/0.5)]">
                    So we can reach you if we need to double-check anything about your collection.
                  </p>
                </div>
              )}

              <button
                onClick={checkout}
                disabled={loading}
                data-testid="cart-checkout"
                className="btn-pill btn-primary w-full mt-8 disabled:opacity-60"
              >
                {loading ? "Redirecting to Stripe…" : "Secure checkout"} <ArrowUpRight size={16} />
              </button>

              <div className="mt-4 flex items-center justify-center gap-3 text-xs text-[hsl(var(--onyx)/0.55)]">
                <span className="rounded-full bg-blush px-3 py-1 font-medium text-[hsl(var(--onyx))]">Klarna</span>
                <span className="rounded-full bg-blush px-3 py-1 font-medium text-[hsl(var(--onyx))]">Clearpay</span>
                <span className="rounded-full bg-blush px-3 py-1 font-medium text-[hsl(var(--onyx))]">Apple Pay</span>
              </div>
              <div className="mt-2 text-xs text-[hsl(var(--onyx)/0.5)] text-center">
                Pay in 3 with Klarna or 4 with Clearpay — spread the cost, interest-free.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
